import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { subscribeToGame, updateGame, getUserProfile, updateUserProfile, removePlayer, migrateHost, startGame, startNextRound, selectTeam, confirmTeamSelection, joinGame } from '../services/firebase';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import Confetti from 'react-confetti';

// Components
import ScoreBoard from '../components/ScoreBoard';
import WordSelectionPhase from '../components/WordSelectionPhase';
import PerformerSelectionPhase from '../components/PerformerSelectionPhase';
import WordRevealPhase from '../components/WordRevealPhase';
import QuickScorePhase from '../components/QuickScorePhase';
import LoadingScreen from '../components/LoadingScreen';
import HostSelectionModal from '../components/HostSelectionModal';
import TeamSelectionPanel from '../components/TeamSelectionPanel';

const GamePlayPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuthStore();
  const [game, setGame] = useState(null);
  const [gamePhase, setGamePhase] = useState('setup'); // setup, wordSelection, performing, scoring, roundComplete, finished
  const [myTeam, setMyTeam] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [showHostSelectionModal, setShowHostSelectionModal] = useState(false);
  const [isSyncingAfterWake, setIsSyncingAfterWake] = useState(false); // Lock during manual sync
  const hasUpdatedStatsRef = useRef(false); // Track if we've already updated stats for this game
  const isStartingGameRef = useRef(false); // Prevent double-calling startGame
  const hasAttemptedAutoJoinRef = useRef(false); // Track if we've attempted auto-join

  // VERSION TAG for debugging deployment
  console.log('🏷️ GamePlayPage VERSION: 2024-12-07-v12-auto-join-fix');

  // Check if current user is a waiting player (joined mid-game)
  const isWaitingPlayer = game?.waitingPlayers?.[user?.uid] !== undefined;
  const teamSelectionMode = game?.teamSelectionMode || 'random';

  // Team selection handlers
  const handleSelectTeam = async (team) => {
    try {
      await selectTeam(gameId, user.uid, team);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleConfirmTeam = async () => {
    try {
      await confirmTeamSelection(gameId, user.uid);
      toast.success('Team confirmed!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Check if host can start next round (for team selection mode)
  const canStartNextRound = () => {
    if (!game || teamSelectionMode !== 'teamSelection') return { can: true, reason: '' };

    const players = game.players || {};
    const waitingPlayers = game.waitingPlayers || {};
    const confirmations = game.playerTeamConfirmations || {};

    // Count confirmed players by team
    let confirmedTeamA = 0;
    let confirmedTeamB = 0;

    // Check active players
    Object.entries(players).forEach(([id, player]) => {
      if (player.isActive && confirmations[id]) {
        if (player.team === 'teamA') confirmedTeamA++;
        else if (player.team === 'teamB') confirmedTeamB++;
      }
    });

    // Check waiting players
    Object.entries(waitingPlayers).forEach(([id, player]) => {
      if (confirmations[id] && player.team) {
        if (player.team === 'teamA') confirmedTeamA++;
        else if (player.team === 'teamB') confirmedTeamB++;
      }
    });

    const totalConfirmed = confirmedTeamA + confirmedTeamB;

    if (totalConfirmed < 4) {
      return { can: false, reason: `Need ${4 - totalConfirmed} more confirmed players` };
    }
    if (confirmedTeamA < 2) {
      return { can: false, reason: `Need ${2 - confirmedTeamA} more in Team A` };
    }
    if (confirmedTeamB < 2) {
      return { can: false, reason: `Need ${2 - confirmedTeamB} more in Team B` };
    }

    return { can: true, reason: '' };
  };

  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-join player if they accessed game URL directly without joining through lobby
  useEffect(() => {
    const autoJoinIfNeeded = async () => {
      // Wait for game data and user to be loaded
      if (!game || !user || !userProfile) return;

      // Only attempt once
      if (hasAttemptedAutoJoinRef.current) return;

      // Check if player is already in the game (either as active or waiting)
      const isInPlayers = game.players?.[user.uid];
      const isInWaiting = game.waitingPlayers?.[user.uid];

      if (isInPlayers || isInWaiting) {
        console.log('✅ Player already in game');
        return;
      }

      // Player is not in the game - auto-join them
      hasAttemptedAutoJoinRef.current = true;

      console.log('🚪 Player not in game - attempting auto-join as waiting player');

      try {
        const playerName = userProfile.displayName || user.email?.split('@')[0] || 'Player';
        await joinGame(gameId, user.uid, playerName);
        console.log('✅ Auto-joined game successfully');
        toast.success('Joined game!');
      } catch (error) {
        console.error('❌ Auto-join failed:', error);
        // If auto-join fails, redirect to lobby
        toast.error('Could not join game. Redirecting to lobby...');
        navigate(`/game/${gameId}`);
      }
    };

    autoJoinIfNeeded();
  }, [game, user, userProfile, gameId, navigate]);

  // Subscribe to game updates
  useEffect(() => {
    if (!gameId) return;

    const unsubscribe = subscribeToGame(gameId, (gameData) => {
      // CRITICAL: Ignore listener updates while manually syncing after screen wake
      // This prevents stale cached data from overwriting fresh server data
      if (isSyncingAfterWake) {
        console.log('⏸️ Firebase listener update IGNORED (manual sync in progress)');
        return;
      }

      console.log('🔔 Firebase listener update received:', {
        gamePhase: gameData.gamePhase,
        status: gameData.status,
        updatedAt: gameData.updatedAt?.toDate?.().toISOString()
      });

      setGame(gameData);

      // Update phase from game data
      if (gameData.gamePhase) {
        setGamePhase(gameData.gamePhase);
      }

      // Get my team
      const myPlayerId = user?.uid;
      if (myPlayerId && gameData.players?.[myPlayerId]) {
        setMyTeam(gameData.players[myPlayerId].team);
      }

      // Check if game ended
      if (gameData.status === 'finished') {
        setGamePhase('finished');
      }

      // Check if player has been removed due to free round limit
      if (user && gameData.players?.[user.uid]?.status === 'removed_free_limit') {
        toast.error('You have used all your free rounds. Please subscribe to continue playing!');
        navigate('/subscription');
      }
    });

    return () => unsubscribe();
  }, [gameId, user, isSyncingAfterWake, navigate]);

  // Page Visibility API: GOLD STANDARD screen wake sync implementation
  // Used by production apps like Slack, Discord, WhatsApp Web
  useEffect(() => {
    if (!gameId) return;

    let lastSyncTime = Date.now();
    let retryTimeoutId = null;
    let isSyncing = false;

    const fetchFreshGameStateFromServer = async (attempt = 1, maxAttempts = 3) => {
      const attemptStartTime = Date.now();
      console.log('');
      console.log('═══════════════════════════════════════════════════════════════');
      console.log(`🔄 SCREEN WAKE SYNC - Attempt ${attempt}/${maxAttempts}`);
      console.log(`   Triggered at: ${new Date().toISOString()}`);
      console.log(`   Time since last sync: ${Math.floor((attemptStartTime - lastSyncTime) / 1000)}s`);
      console.log(`   Current LOCAL gamePhase: ${gamePhase}`);
      console.log(`   Current LOCAL game data:`, game ? {
        gamePhase: game.gamePhase,
        status: game.status,
        currentRound: game.currentRound,
        updatedAt: game.updatedAt
      } : 'null');
      console.log('═══════════════════════════════════════════════════════════════');

      try {
        // CRITICAL: Use getDocFromServer() to bypass ALL caches (local + Firebase)
        console.log('📡 Step 1: Importing Firebase SDK...');
        const { getDocFromServer, doc, db } = await import('../services/firebase');
        console.log('✓ Firebase SDK imported');

        console.log('📡 Step 2: Creating game document reference...');
        const gameRef = doc(db, 'games', gameId);
        console.log(`✓ Game ref created for gameId: ${gameId}`);

        console.log('📡 Step 3: Fetching from SERVER (bypassing all caches)...');
        const fetchStartTime = Date.now();
        const snapshot = await getDocFromServer(gameRef); // FORCE SERVER FETCH
        const fetchDuration = Date.now() - fetchStartTime;
        console.log(`✓ Server fetch completed in ${fetchDuration}ms`);

        if (!snapshot.exists()) {
          console.error('❌ Game document does not exist in Firebase!');
          return false;
        }

        const serverData = snapshot.data();
        const serverTimestamp = serverData.updatedAt?.toDate?.() || new Date();

        console.log('');
        console.log('📦 SERVER DATA RECEIVED:');
        console.log('   gamePhase:', serverData.gamePhase);
        console.log('   status:', serverData.status);
        console.log('   currentRound:', serverData.currentRound);
        console.log('   updatedAt:', serverTimestamp.toISOString());
        console.log('   currentRoundData:', {
          wordSelectingTeam: serverData.currentRoundData?.wordSelectingTeam,
          performingTeam: serverData.currentRoundData?.performingTeam,
          selectedWord: serverData.currentRoundData?.selectedWord,
          wordRevealed: serverData.currentRoundData?.wordRevealed,
          timerStartedAt: serverData.currentRoundData?.timerStartedAt,
          scored: serverData.currentRoundData?.scored,
          scoredBy: serverData.currentRoundData?.scoredBy
        });
        console.log('   scores:', serverData.scores);

        // DATA FRESHNESS VALIDATION
        const localTimestamp = game?.updatedAt?.toDate?.() || new Date(0);
        const isServerDataNewer = serverTimestamp > localTimestamp;
        const timeDifference = Math.abs(serverTimestamp - localTimestamp) / 1000;

        console.log('');
        console.log('🔍 DATA FRESHNESS CHECK:');
        console.log(`   Local timestamp:  ${localTimestamp.toISOString()}`);
        console.log(`   Server timestamp: ${serverTimestamp.toISOString()}`);
        console.log(`   Time difference: ${timeDifference.toFixed(1)}s`);
        console.log(`   Is server data newer? ${isServerDataNewer ? 'YES ✓' : 'NO ✗'}`);

        // PHASE CHANGE DETECTION
        const phaseChanged = game && game.gamePhase !== serverData.gamePhase;
        console.log('');
        console.log('🔄 PHASE CHANGE DETECTION:');
        console.log(`   Local phase:  ${game?.gamePhase || 'null'}`);
        console.log(`   Server phase: ${serverData.gamePhase}`);
        console.log(`   Phase changed? ${phaseChanged ? 'YES ✓' : 'NO'}`);

        if (phaseChanged) {
          console.log(`   🚨 PHASE TRANSITION DETECTED: ${game.gamePhase} → ${serverData.gamePhase}`);
        }

        // TIMER EXPIRY DETECTION
        // Check if timer should have expired but phase is still 'performing'
        if (serverData.gamePhase === 'performing' && serverData.currentRoundData?.timerStartedAt) {
          const timerStartedAt = new Date(serverData.currentRoundData.timerStartedAt).getTime();
          const now = Date.now();
          const elapsedSeconds = Math.floor((now - timerStartedAt) / 1000);
          const timerDuration = game?.settings?.timer || 60; // Default 60s if not set

          console.log('');
          console.log('⏰ TIMER EXPIRY CHECK:');
          console.log(`   Timer started at: ${new Date(timerStartedAt).toISOString()}`);
          console.log(`   Timer duration: ${timerDuration}s`);
          console.log(`   Elapsed time: ${elapsedSeconds}s`);
          console.log(`   Time remaining: ${Math.max(0, timerDuration - elapsedSeconds)}s`);

          if (elapsedSeconds >= timerDuration) {
            console.log('');
            console.log('🚨 TIMER EXPIRED BUT PHASE STILL "PERFORMING"!');
            console.log(`   Timer expired ${elapsedSeconds - timerDuration}s ago`);
            console.log('   This indicates server is in transition state');
            console.log('   🔄 Will poll for phase change to "scoring"...');

            // Poll for phase change every second for up to 5 attempts
            for (let pollAttempt = 1; pollAttempt <= 5; pollAttempt++) {
              console.log('');
              console.log(`📡 Poll attempt ${pollAttempt}/5 - Checking for phase change...`);

              // Wait 1 second before polling
              await new Promise(resolve => setTimeout(resolve, 1000));

              const pollSnapshot = await getDocFromServer(gameRef);
              const pollData = pollSnapshot.data();

              console.log(`   Server gamePhase: ${pollData.gamePhase}`);

              if (pollData.gamePhase !== 'performing') {
                console.log(`   ✅ Phase changed to "${pollData.gamePhase}"!`);
                console.log('   Updating local state with latest data...');

                // Update state with the latest polled data
                setGame(pollData);
                setGamePhase(pollData.gamePhase);
                if (pollData.status === 'finished') {
                  setGamePhase('finished');
                }

                // Update team
                const myPlayerId = user?.uid;
                if (myPlayerId && pollData.players?.[myPlayerId]) {
                  setMyTeam(pollData.players[myPlayerId].team);
                }

                console.log('   ✅ State updated successfully with polled data');
                console.log('');
                console.log('═══════════════════════════════════════════════════════════════');
                console.log('✅ SYNC SUCCESSFUL (via polling after timer expiry)');
                console.log('═══════════════════════════════════════════════════════════════');
                console.log('');

                return true; // Exit function early, polling succeeded
              }

              console.log(`   ⏳ Still in "performing" phase, will retry...`);
            }

            console.log('');
            console.log('⚠️ Max poll attempts reached - phase still "performing"');
            console.log('   Proceeding with server data as-is');
          } else {
            console.log(`   ✓ Timer has not expired yet (${timerDuration - elapsedSeconds}s remaining)`);
          }
        }

        // UPDATE LOCAL STATE WITH SERVER DATA
        console.log('');
        console.log('💾 UPDATING LOCAL STATE...');
        setGame(serverData);
        console.log('✓ setGame() called with server data');

        if (serverData.gamePhase) {
          setGamePhase(serverData.gamePhase);
          console.log(`✓ setGamePhase() called: ${serverData.gamePhase}`);
        }

        if (serverData.status === 'finished') {
          setGamePhase('finished');
          console.log('✓ setGamePhase("finished") called');
        }

        // Update my team
        const myPlayerId = user?.uid;
        if (myPlayerId && serverData.players?.[myPlayerId]) {
          setMyTeam(serverData.players[myPlayerId].team);
          console.log(`✓ setMyTeam() called: ${serverData.players[myPlayerId].team}`);
        }

        const totalSyncTime = Date.now() - attemptStartTime;
        console.log('');
        console.log('═══════════════════════════════════════════════════════════════');
        console.log(`✅ SYNC SUCCESSFUL - Total time: ${totalSyncTime}ms`);
        console.log('═══════════════════════════════════════════════════════════════');
        console.log('');

        return true;

      } catch (err) {
        const totalSyncTime = Date.now() - attemptStartTime;
        console.log('');
        console.log('═══════════════════════════════════════════════════════════════');
        console.error(`❌ SYNC FAILED - Attempt ${attempt}/${maxAttempts}`);
        console.error(`   Error type: ${err.name}`);
        console.error(`   Error message: ${err.message}`);
        console.error(`   Total time: ${totalSyncTime}ms`);
        console.error(`   Full error:`, err);
        console.log('═══════════════════════════════════════════════════════════════');

        // Retry with exponential backoff
        if (attempt < maxAttempts) {
          const delayMs = 1000 * attempt; // 1s, 2s, 3s
          console.log(`⏱️ Retrying in ${delayMs}ms...`);
          console.log('');

          return new Promise((resolve) => {
            retryTimeoutId = setTimeout(async () => {
              const success = await fetchFreshGameStateFromServer(attempt + 1, maxAttempts);
              resolve(success);
            }, delayMs);
          });
        } else {
          console.error('❌ MAX RETRY ATTEMPTS REACHED - Sync abandoned');
          console.log('');
          return false;
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('');
        console.log('👁️👁️👁️ SCREEN/TAB BECAME VISIBLE 👁️👁️👁️');
        console.log(`   Time: ${new Date().toISOString()}`);
        console.log(`   Navigator online: ${navigator.onLine}`);
        console.log(`   Document visibility: ${document.visibilityState}`);

        // Prevent concurrent sync operations
        if (isSyncing) {
          console.log('⚠️ Sync already in progress, skipping...');
          return;
        }

        isSyncing = true;
        setIsSyncingAfterWake(true); // LOCK: Prevent listener from overwriting fresh data
        console.log('🔒 Sync lock ENGAGED - Firebase listener updates will be ignored');

        // Small delay to let network stabilize
        setTimeout(async () => {
          try {
            await fetchFreshGameStateFromServer();
          } finally {
            isSyncing = false;
            lastSyncTime = Date.now();
            // Release lock after a brief delay to ensure our fresh data has been applied
            setTimeout(() => {
              setIsSyncingAfterWake(false);
              console.log('🔓 Sync lock RELEASED - Firebase listener updates will resume');
            }, 500);
          }
        }, 300); // Reduced from 500ms to be more responsive

      } else {
        console.log('');
        console.log('🌙🌙🌙 SCREEN/TAB HIDDEN 🌙🌙🌙');
        console.log(`   Time: ${new Date().toISOString()}`);
        console.log(`   Current gamePhase: ${gamePhase}`);
        console.log('   ⚠️ Firebase listeners may be throttled while hidden');
        console.log('');

        // Clear any pending retries
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          retryTimeoutId = null;
        }
        isSyncing = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [gameId, user, gamePhase, game]);

  // Initialize game - assign teams and start first round
  useEffect(() => {
    if (game && game.status === 'playing' && !game.teamsAssigned && game.hostId === user?.uid) {
      // Prevent double-calling due to React re-renders / Firebase listener race conditions
      if (isStartingGameRef.current) {
        console.log('⏸️ startGame already in progress, skipping duplicate call');
        return;
      }
      isStartingGameRef.current = true;
      assignTeams().finally(() => {
        // Reset after a delay to allow Firebase to sync
        setTimeout(() => {
          isStartingGameRef.current = false;
        }, 2000);
      });
    }
  }, [game, user]);

  // Update stats for ALL players when game ends (not just host)
  useEffect(() => {
    const updatePlayerStats = async () => {
      // Only update once per game, and only when game is finished
      if (!game || game.status !== 'finished' || !user?.uid || hasUpdatedStatsRef.current) {
        return;
      }

      // Check if this player is actually in the game
      if (!game.players?.[user.uid]) {
        console.log('Player not in game, skipping stats update');
        return;
      }

      // Mark as updated to prevent duplicate updates
      hasUpdatedStatsRef.current = true;

      console.log('📊 Updating player stats for game end (all players)');

      try {
        const currentStats = await getUserProfile(user.uid);

        // Determine if player won
        const playerTeam = game.players[user.uid]?.team;
        const didWin = (playerTeam === 'teamA' && game.scores?.teamA > game.scores?.teamB) ||
                       (playerTeam === 'teamB' && game.scores?.teamB > game.scores?.teamA);

        // Calculate points earned
        const pointsEarned = playerTeam === 'teamA' ? (game.scores?.teamA || 0) : (game.scores?.teamB || 0);

        // Update user stats
        await updateUserProfile(user.uid, {
          gamesPlayed: (currentStats?.gamesPlayed || 0) + 1,
          gamesWon: didWin ? (currentStats?.gamesWon || 0) + 1 : (currentStats?.gamesWon || 0),
          totalPoints: (currentStats?.totalPoints || 0) + pointsEarned,
          xp: (currentStats?.xp || 0) + 50 // Base XP for completing game
        });

        // Note: freeGamesRemaining is now decremented per-round by Cloud Functions
        // (startGame for round 1, startNextRound for subsequent rounds)

        console.log('✅ Player stats updated successfully', {
          gamesPlayed: (currentStats?.gamesPlayed || 0) + 1,
          didWin,
          pointsEarned
        });

        setShowConfetti(true);
      } catch (error) {
        console.error('Error updating player stats:', error);
      }
    };

    updatePlayerStats();
  }, [game?.status, user?.uid]);

  const assignTeams = async () => {
    try {
      console.log('🎮 Starting game via Cloud Function...');
      const result = await startGame({ gameId });

      console.log('🎮 startGame result:', result.data);

      if (result.data.success === false) {
        if (result.data.reason === 'insufficient_players') {
          toast.error(result.data.message || 'Game ended early - not enough players to continue (minimum 4 total and at least 2 players per team)');
          return;
        }
      }

      // Check if current user was removed
      if (result.data.playersRemoved?.includes(user.uid)) {
        toast.error('You have used all your free rounds. Please subscribe to continue playing!');
        navigate('/subscription');
        return;
      }

      if (result.data.playersRemoved?.length > 0) {
        toast(`${result.data.playersRemoved.length} player(s) removed due to free round limit`);
      }

      toast.success('Teams assigned! Game starting!');
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Failed to start game: ' + error.message);
    }
  };

  const handleRoundComplete = async () => {
    try {
      console.log('🎮 Starting next round via Cloud Function...');
      const result = await startNextRound({ gameId });

      console.log('🎮 startNextRound result:', result.data);

      if (result.data.success === false) {
        if (result.data.reason === 'insufficient_players') {
          toast.error(result.data.message || 'Game ended early - not enough players to continue (minimum 4 total and at least 2 players per team)');
          return;
        }
      }

      // Check if current user was removed
      if (result.data.playersRemoved?.includes(user.uid)) {
        toast.error('You have used all your free rounds. Please subscribe to continue playing!');
        navigate('/subscription');
        return;
      }

      if (result.data.playersRemoved?.length > 0) {
        toast(`${result.data.playersRemoved.length} player(s) removed due to free round limit`);
      }

      toast.success(`Starting Round ${result.data.nextRound}!`);
    } catch (error) {
      console.error('Error starting next round:', error);
      toast.error('Failed to start next round: ' + error.message);
    }
  };

  const handleTimerComplete = async () => {
    try {
      // Add a safety check to prevent a late timer from overwriting the game state.
      const { getDoc, doc, db } = await import('../services/firebase');
      const gameRef = doc(db, 'games', gameId);
      const freshGameDoc = await getDoc(gameRef);

      if (freshGameDoc.exists() && freshGameDoc.data().gamePhase === 'performing') {
        await updateGame(gameId, {
          gamePhase: 'scoring'
        });
      } else {
        console.log('🛑 Timer complete handler aborted: Game is no longer in the performing phase.');
      }
    } catch (error) {
      console.error('Error moving to scoring:', error);
    }
  };

  const handleEndGame = async () => {
    try {
      // Just update game status - stats are now updated for ALL players via useEffect
      await updateGame(gameId, {
        status: 'finished'
      });

      toast.success('Game ended!');
    } catch (error) {
      console.error('Error ending game:', error);
      toast.error('Failed to end game');
    }
  };

  // Handle regular player leaving game
  const handleLeaveGame = async () => {
    try {
      const result = await removePlayer(gameId, user.uid, 'left_voluntarily');

      if (result?.gameEnded) {
        toast.success('Game ended - not enough players to continue (minimum 4 required)');
      } else {
        toast.success(`You left the game`);
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error leaving game:', error);
      toast.error('Failed to leave game');
    }
  };

  // Handle host leaving game (show modal to select new host)
  const handleHostLeaveGame = () => {
    setShowHostSelectionModal(true);
  };

  // Handle host selection from modal
  const handleSelectNewHost = async (selectedHostId) => {
    try {
      const result = await migrateHost(gameId, user.uid, selectedHostId);

      if (result.gameEnded) {
        toast.success('Game ended - you were the last player');
      } else {
        toast.success(`${result.newHostName} is now the host`);
      }

      // Remove current host from game
      await removePlayer(gameId, user.uid, 'left_voluntarily');
      setShowHostSelectionModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error migrating host:', error);
      toast.error('Failed to leave game');
    }
  };

  // Handle host disconnect (auto-migrate)
  const handleHostDisconnect = async () => {
    if (!game?.hostId || game.hostId === user.uid) return; // Don't run for current user

    try {
      console.log('🔄 Auto-migrating host due to disconnect...');
      const result = await migrateHost(gameId, game.hostId, null);

      if (result.gameEnded) {
        toast('Host left and game ended - no players remaining');
      } else if (result.newHostId === user.uid) {
        toast.success(`🎖️ You are now the host!`);
      } else {
        toast(`Host left. ${result.newHostName} is now the host`);
      }

      // Remove disconnected host
      await removePlayer(gameId, game.hostId, 'disconnected');
    } catch (error) {
      console.error('Error handling host disconnect:', error);
    }
  };

  // Handle performer disconnect during performance (restart round, no points)
  const handlePerformerDisconnect = async () => {
    if (!game?.currentRoundData?.performerId) return;

    const performerId = game.currentRoundData.performerId;
    const performerName = game.players?.[performerId]?.name || 'Performer';

    try {
      console.log(`⚠️ ${performerName} disconnected during performance - restarting round`);

      // Remove disconnected performer
      await removePlayer(gameId, performerId, 'disconnected_during_performance');

      // Restart current round (no points awarded)
      await updateGame(gameId, {
        gamePhase: 'wordSelection',
        'currentRoundData.wordOptions': null,
        'currentRoundData.selectedWord': null,
        'currentRoundData.performerId': null,
        'currentRoundData.wordRevealed': false,
        'currentRoundData.timerStartedAt': null,
        'currentRoundData.scored': null,
        'currentRoundData.scoredBy': null,
        'currentRoundData.correctlyGuessed': null,
        'currentRoundData.guessedBy': null,
        'currentRoundData.guessedByName': null,
        'currentRoundData.guessedByTeam': null,
        'currentRoundData.guesses': {}
      });

      toast(`${performerName} disconnected. Round restarted with no points awarded.`);
    } catch (error) {
      console.error('Error handling performer disconnect:', error);
    }
  };

  if (!game) {
    return <LoadingScreen />;
  }

  const isHost = game.hostId === user?.uid;
  const currentRound = game.currentRound || 1;

  // Game hasn't started yet
  if (!game.teamsAssigned) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '3rem', maxWidth: '500px' }}>
          <div className="spinner" style={{ margin: '0 auto 2rem' }}></div>
          <h2 style={{ marginBottom: '1rem' }}>Preparing Game...</h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            {isHost ? 'Assigning teams and setting up the first round...' : 'Host is preparing the game...'}
          </p>
        </div>
      </div>
    );
  }

  // Waiting player view (joined mid-game, waiting for round to end)
  if (isWaitingPlayer && gamePhase !== 'roundComplete' && gamePhase !== 'finished') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '3rem', maxWidth: '500px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>⏳</div>
          <h2 style={{ marginBottom: '1rem' }}>Waiting for Round to End</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            A round is currently in progress. You'll be able to join once it finishes.
          </p>

          {/* Show current game status */}
          <div style={{
            padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Game Code
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              color: 'var(--primary)'
            }}>
              {gameId}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              padding: '1rem',
              background: 'rgba(99, 102, 241, 0.1)',
              borderRadius: 'var(--radius)'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                {game.scores?.teamA || 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Team A</div>
            </div>
            <div style={{
              padding: '1rem',
              background: 'rgba(236, 72, 153, 0.1)',
              borderRadius: 'var(--radius)'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--secondary)' }}>
                {game.scores?.teamB || 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Team B</div>
            </div>
          </div>

          <div style={{
            padding: '0.75rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius)',
            fontSize: '0.875rem',
            color: 'var(--text-secondary)'
          }}>
            Round {currentRound} in progress...
          </div>
        </div>
      </div>
    );
  }

  // Game Finished
  if (gamePhase === 'finished') {
    const teamAScore = game.scores?.teamA || 0;
    const teamBScore = game.scores?.teamB || 0;
    const winner = teamAScore > teamBScore ? 'Team A' : teamBScore > teamAScore ? 'Team B' : 'Tie';
    const didWin = (winner === 'Team A' && myTeam === 'teamA') || (winner === 'Team B' && myTeam === 'teamB');
    const endedDueToInsufficientPlayers = game.endReason === 'insufficient_players';

    // Create individual player rankings
    const playerRankings = Object.entries(game.players || {}).map(([playerId, player]) => ({
      id: playerId,
      name: player.name,
      team: player.team,
      points: game.playerPoints?.[playerId] || 0
    })).sort((a, b) => b.points - a.points);

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        {showConfetti && <Confetti width={windowSize.width} height={windowSize.height} />}

        <div className="card" style={{ textAlign: 'center', padding: '3rem', maxWidth: '600px' }}>
          {endedDueToInsufficientPlayers && (
            <div style={{
              padding: '1rem',
              background: '#fef3c7',
              color: '#92400e',
              borderRadius: 'var(--radius-lg)',
              marginBottom: '2rem',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              ⚠️ {game.endMessage || 'Game ended early - not enough players to continue (minimum 4 total and at least 2 players per team)'}
            </div>
          )}

          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>
            {winner === 'Tie' ? '🤝' : '🏆'}
          </div>

          <h1 style={{ marginBottom: '1rem' }}>
            {winner === 'Tie' ? "It's a Tie!" : `${winner} Wins!`}
          </h1>

          {didWin && winner !== 'Tie' && (
            <div style={{
              padding: '1rem',
              background: '#d1fae5',
              color: '#065f46',
              borderRadius: 'var(--radius-lg)',
              marginBottom: '2rem',
              fontWeight: 700,
              fontSize: '1.125rem'
            }}>
              🎉 Congratulations! You won!
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              padding: '1.5rem',
              background: myTeam === 'teamA' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-lg)'
            }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 800,
                color: 'var(--primary)',
                marginBottom: '0.5rem'
              }}>
                {teamAScore}
              </div>
              <div style={{ fontWeight: 600 }}>
                Team A {myTeam === 'teamA' && '(You)'}
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: myTeam === 'teamB' ? 'rgba(236, 72, 153, 0.1)' : 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-lg)'
            }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 800,
                color: 'var(--secondary)',
                marginBottom: '0.5rem'
              }}>
                {teamBScore}
              </div>
              <div style={{ fontWeight: 600 }}>
                Team B {myTeam === 'teamB' && '(You)'}
              </div>
            </div>
          </div>

          {/* Individual Player Rankings */}
          <div style={{
            marginBottom: '2rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(90deg, var(--primary), var(--secondary))',
              color: 'white',
              fontWeight: 700,
              textAlign: 'center'
            }}>
              🏆 Individual Rankings
            </div>
            <div style={{ padding: '0.5rem' }}>
              {playerRankings.map((player, index) => (
                <div
                  key={player.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    background: player.id === user.uid ? 'rgba(99, 102, 241, 0.1)' : 'white',
                    borderRadius: 'var(--radius)',
                    marginBottom: '0.5rem',
                    border: player.id === user.uid ? '2px solid var(--primary)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      color: index === 0 ? '#f59e0b' : index === 1 ? '#9ca3af' : index === 2 ? '#cd7f32' : 'var(--text-tertiary)',
                      minWidth: '2rem'
                    }}>
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                    </div>
                    <div>
                      <div style={{
                        fontWeight: player.id === user.uid ? 700 : 600,
                        fontSize: '1rem',
                        marginBottom: '0.125rem'
                      }}>
                        {player.name} {player.id === user.uid && '(You)'}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: player.team === 'teamA' ? 'var(--primary)' : 'var(--secondary)',
                        fontWeight: 600
                      }}>
                        Team {player.team === 'teamA' ? 'A' : 'B'}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: 'var(--primary)'
                  }}>
                    {player.points}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            marginBottom: '2rem',
            padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius)',
            fontSize: '0.875rem',
            color: 'var(--text-secondary)'
          }}>
            Played {currentRound - 1} rounds
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <a href="/dashboard" className="btn btn-primary btn-full btn-lg">
              Back to Dashboard
            </a>
            <a href="/create" className="btn btn-outline btn-full">
              Play Again
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Main gameplay
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: '2rem' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '1.5rem 0',
        marginBottom: '1.5rem'
      }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem'
          }}>
            <div>
              <h2 style={{ color: 'white', fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                Round {currentRound}
              </h2>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                Game: {gameId}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isHost ? (
                <>
                  <button
                    onClick={handleHostLeaveGame}
                    className="btn btn-sm"
                    style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
                  >
                    🚪 Leave Game
                  </button>
                  <button
                    onClick={handleEndGame}
                    className="btn btn-sm"
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                  >
                    End Game
                  </button>
                </>
              ) : (
                <button
                  onClick={handleLeaveGame}
                  className="btn btn-sm"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  🚪 Leave Game
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: '800px' }}>
        {/* Scoreboard */}
        <ScoreBoard game={game} myTeam={myTeam} />

        {/* Game Phases */}
        {gamePhase === 'wordSelection' && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {/* Team A - Word Selection */}
            {myTeam === game.currentRoundData.wordSelectingTeam ? (
              <WordSelectionPhase
                game={game}
                userId={user.uid}
                onComplete={() => {}}
              />
            ) : (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-xl)',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: 'var(--shadow-md)'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                <h3 style={{ marginBottom: '0.5rem' }}>Waiting for Team {game.currentRoundData.wordSelectingTeam === 'teamA' ? 'A' : 'B'}</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  They're selecting a word...
                </p>
              </div>
            )}

            {/* Team B - Performer Selection */}
            {myTeam === game.currentRoundData.performingTeam && (
              <PerformerSelectionPhase game={game} userId={user.uid} />
            )}
          </div>
        )}

        {gamePhase === 'performing' && (
          <WordRevealPhase
            game={game}
            userId={user.uid}
            onTimerComplete={handleTimerComplete}
            onEndGame={isHost ? handleEndGame : null}
          />
        )}

        {gamePhase === 'scoring' && (
          <QuickScorePhase
            game={game}
            userId={user.uid}
            onComplete={handleRoundComplete}
          />
        )}

        {gamePhase === 'roundComplete' && (
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: '2rem',
            boxShadow: 'var(--shadow-md)',
            textAlign: 'center'
          }}>
            <div style={{
              padding: '2rem',
              background: '#d1fae5',
              borderRadius: 'var(--radius-lg)',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '0.5rem' }}>🎉</div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#065f46',
                marginBottom: '0.5rem'
              }}>
                Round {currentRound} Complete!
              </div>
              {game.currentRoundData?.guessedByName && (
                <div style={{
                  fontSize: '1rem',
                  color: '#065f46',
                  opacity: 0.9
                }}>
                  {game.currentRoundData.guessedByName} guessed it correctly!
                </div>
              )}
            </div>

            {/* Show the word that was selected */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 'var(--radius-lg)',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '0.875rem',
                opacity: 0.9,
                marginBottom: '0.5rem',
                fontWeight: 600
              }}>
                The word was:
              </div>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {game.currentRoundData?.selectedWord || 'N/A'}
              </div>
            </div>

            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius)',
              fontSize: '0.875rem'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Current Scores</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', fontSize: '1.25rem', fontWeight: 700 }}>
                <div>Team A: {game.scores?.teamA || 0}</div>
                <div>Team B: {game.scores?.teamB || 0}</div>
              </div>
            </div>

            {/* Show waiting players who joined mid-game */}
            {Object.keys(game.waitingPlayers || {}).length > 0 && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: 'var(--radius)',
                fontSize: '0.875rem'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--primary)' }}>
                  New Players Joining
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {Object.values(game.waitingPlayers).map(p => p.name).join(', ')}
                </div>
              </div>
            )}

            {/* Team Selection Panel (for team selection mode) */}
            {teamSelectionMode === 'teamSelection' && (
              <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '0.75rem',
                  textAlign: 'center',
                  color: 'var(--text-secondary)'
                }}>
                  {isWaitingPlayer ? 'Select your team to join next round' : 'Switch teams or confirm to continue'}
                </div>
                <TeamSelectionPanel
                  game={{
                    ...game,
                    // Merge waiting players into players for display
                    players: {
                      ...game.players,
                      ...game.waitingPlayers
                    }
                  }}
                  userId={user?.uid}
                  onSelectTeam={handleSelectTeam}
                  onConfirm={handleConfirmTeam}
                  showConfirmButton={true}
                  disabled={false}
                  compact={true}
                />
              </div>
            )}

            {/* Host: Start next round button */}
            {isHost && (
              <>
                {teamSelectionMode === 'teamSelection' ? (
                  <>
                    {canStartNextRound().can ? (
                      <button
                        onClick={handleRoundComplete}
                        className="btn btn-primary btn-full btn-lg"
                      >
                        Start Round {currentRound + 1} →
                      </button>
                    ) : (
                      <button
                        disabled
                        className="btn btn-primary btn-full btn-lg"
                        style={{ opacity: 0.6 }}
                      >
                        {canStartNextRound().reason}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleRoundComplete}
                    className="btn btn-primary btn-full btn-lg"
                  >
                    Start Round {currentRound + 1} →
                  </button>
                )}
              </>
            )}

            {/* Non-host waiting message */}
            {!isHost && (
              <div style={{
                padding: '1rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                {teamSelectionMode === 'teamSelection'
                  ? 'Confirm your team selection, then wait for host to start next round...'
                  : 'Waiting for host to start next round...'}
              </div>
            )}
          </div>
        )}

        {/* Debug Info (remove in production) */}
        <details style={{ marginTop: '2rem', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
          <summary style={{ cursor: 'pointer', padding: '0.5rem' }}>Debug Info</summary>
          <pre style={{
            padding: '1rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius)',
            overflow: 'auto',
            fontSize: '0.75rem'
          }}>
            {JSON.stringify({
              gamePhase,
              myTeam,
              currentRound,
              wordSelectingTeam: game.currentRoundData?.wordSelectingTeam,
              performingTeam: game.currentRoundData?.performingTeam,
              selectedWord: game.currentRoundData?.selectedWord,
              performerId: game.currentRoundData?.performerId,
              scores: game.scores
            }, null, 2)}
          </pre>
        </details>
      </div>

      {/* Host Selection Modal */}
      <HostSelectionModal
        isOpen={showHostSelectionModal}
        game={game}
        currentHostId={user?.uid}
        onSelectHost={handleSelectNewHost}
        onCancel={() => setShowHostSelectionModal(false)}
      />
    </div>
  );
};

export default GamePlayPage;
